/**
 * ScanErrorModal - Blocking error modal for scan errors
 *
 * Unlike toasts, this modal:
 * - Greys out the background (AlertDialog overlay)
 * - Requires explicit dismissal (OK button or Enter key)
 * - Cannot be auto-dismissed
 * - Is large and impossible to miss in warehouse environment
 *
 * Scanner Enter is consumed by QRScannerInput's preventDefault(); Radix
 * AlertDialog + autoFocus otherwise handles Enter/Space on the OK button
 * automatically — no artificial delay is added there.
 *
 * Mash guard: a held/repeated Enter or Space carried over from whatever the
 * operator was doing right before this modal opened (e.g. a held placement
 * confirm) must not dismiss an unread error. While open, a capture-phase
 * listener swallows Enter/Space until either a keyup for that key has been
 * observed since open, or 400ms have elapsed — whichever comes first. A
 * single fresh press still works immediately once armed; this only closes
 * the window where a key that was already down before open can auto-fire.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';

interface ScanErrorModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  /** Optional secondary message (e.g., replacement garment location) */
  secondaryMessage?: string;
  /** `success` is a terminal ACKNOWLEDGEMENT, not a problem — a clean bulk run
   *  the operator must dismiss (PRODUCTION_DESIGN §10). Emerald, no error beep. */
  severity?: 'error' | 'warning' | 'success';
}

export function ScanErrorModal({
  open,
  onClose,
  title,
  message,
  secondaryMessage,
  severity = 'error'
}: ScanErrorModalProps) {
  const { t } = useTranslation('production');
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Focus button when modal opens (ref-based, not autoFocus+disabled)
  useEffect(() => {
    if (!open) return;

    // Small delay ensures modal is rendered before focusing
    const timer = setTimeout(() => {
      buttonRef.current?.focus();
    }, 10);
    return () => clearTimeout(timer);
  }, [open]);

  // Mash guard (see file header): swallow Enter/Space until this key has been
  // released once since open, or 400ms have passed — whichever is first.
  useEffect(() => {
    if (!open) return;
    let enterArmed = false;
    let spaceArmed = false;

    const armTimer = setTimeout(() => {
      enterArmed = true;
      spaceArmed = true;
    }, 400);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !enterArmed) {
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === ' ' && !spaceArmed) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Enter') enterArmed = true;
      else if (e.key === ' ') spaceArmed = true;
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [open]);

  const Icon = severity === 'error' ? AlertCircle : severity === 'success' ? CheckCircle : AlertTriangle;
  const colorClass = severity === 'error'
    ? 'text-red-700 dark:text-red-400'
    : severity === 'success'
      ? 'text-emerald-700 dark:text-emerald-400'
      : 'text-amber-700 dark:text-amber-400';
  const buttonClass = severity === 'success'
    ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-600 focus-visible:ring-emerald-600'
    : severity === 'error'
    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600 focus-visible:ring-red-600'
    : 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-600 focus-visible:ring-amber-600';

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="max-w-lg sm:max-w-xl">
        <AlertDialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <Icon className={cn('h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0', colorClass)} aria-hidden="true" />
            <AlertDialogTitle className={cn('text-2xl sm:text-3xl font-bold', colorClass)}>
              {/* Screen reader severity announcement */}
              <span className="sr-only">
                {severity === 'error'
                  ? t('scanFeedback.error')
                  : severity === 'success'
                    ? t('scanFeedback.success')
                    : t('scanFeedback.warning')}
              </span>
              {title}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-lg sm:text-xl text-foreground">
            {message}
          </AlertDialogDescription>
          {secondaryMessage && (
            <p className="text-base sm:text-lg text-muted-foreground bg-muted/50 p-3 rounded-md whitespace-pre-line max-h-[40vh] overflow-y-auto">
              {secondaryMessage}
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            ref={buttonRef}
            className={cn(
              'h-16 text-lg px-8 w-full sm:w-auto min-w-[120px]',
              'text-white font-semibold',
              buttonClass
            )}
            onClick={onClose}
          >
            {t('scanFeedback.ok')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
